// Quiq SDK docs: https://developers.goquiq.com/docs/webchat/#/reference/types
// Quiq API docs: https://developers.goquiq.com/api/docs

import { CookieManager } from "@Utilities/CookieManager";
import { EngagementProvider } from "@Base/Engagement/Engagement";
import { IQuiqOptions } from "./IQuiqOptions";
import { TextResource } from "@Utilities/TextResource";

import deepmerge from "deepmerge";
import axios from "axios";

// Declare Existing Vars.
declare let Quiq;
export class ChatBase
{
	private ChatCacheCookieName: string;
	private ChatQueryInterval: number;
	private ContactContainers: NodeListOf<Element>;
	private ContactLaunchers: NodeListOf<Element>;
	private readonly engagementProvider: EngagementProvider;
	private LaunchButtons: Array<string>;
	private LaunchContainers: NodeListOf<Element>;
	private OfflineButtonSelector: string;
	private OnlineButtonSelector: string;
	private QuiqChat: any;
	private QuiqOptions: IQuiqOptions;
	private QuiqSDKUrl: string;

	constructor()
	{
		this.engagementProvider = new EngagementProvider();

		// Time in ms to check for chat availability when offline on initial page load (matching quiq default)
		this.ChatQueryInterval = 30000;

		// Quiq SDK url
		this.QuiqSDKUrl = "https://fun.quiq-api.com/app/webchat/index.js";
		this.ChatCacheCookieName = "CHATCACHE_V1";
		this.ContactContainers = document.querySelectorAll(".contact");
		this.ContactLaunchers = document.querySelectorAll(".contact__launcher");
		this.LaunchButtons = [];
		this.LaunchContainers = document.querySelectorAll("[data-chatid=quiq]:not(.contact)");
		this.OfflineButtonSelector = ".stlc-ofl";
		this.OnlineButtonSelector = ".stlc-onl";

		const background = "#f9f9f9";
		const bodyText = "#333";
		const chatFontWeight = "500";
		const grayBorder = "1px solid #ddd";
		const messageRadius = "10px 10px 10px 0";
		const pillRadius = "50rem";
		const weightBold = "600";
		const white = "#fff";

		let bottomPosition = "25px";
		let titleText = "Customer Support";
		let headerText = "Ask Us Anything";

		this.LaunchContainers.forEach(launchContainer =>
		{
			this.LaunchButtons.push(`#${launchContainer.getAttribute("id")} .stlc-onl`);
		});

		this.ContactContainers.forEach(contactContainer =>
		{
			this.LaunchButtons.push(`#${contactContainer.getAttribute("id")} .contact__chat`);
		});

		titleText = TextResource.GetTextByCulture("Feature_Chat_CustomerSupport");
		headerText = TextResource.GetTextByCulture("Feature_Chat_AskAnything");

		// @ts-expect-error Checking for google customer review presence and moving the quiq window up if it is
		if (typeof window.renderBadge !== "undefined")
		{
			bottomPosition = "79px";
		}

		/*
		* These options are provided to the Quiq chat object on initialization.
		* NOTE: Chat options can be overridden at the site level for various look-and-feel details.
		*
		* See the full list of available options that can be configured for Quiq chat:
		* https://developers.goquiq.com/docs/webchat/#/reference/types
		*/
		this.QuiqOptions = {
			contactPoint: "funcom",
			showDefaultLaunchButton: false,
			customLaunchButtons: this.LaunchButtons,
			defaultAgentAvatar: `${window.location.origin}/wwwroot/img/chat/avatar.png`,
			webFontUrls: [`${window.location.origin}/wwwroot/font/MonsterFont.css`],
			fontFamily: `"MonsterFont", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"`,
			width: "min(calc(100% - 50px), 450px)",
			height: "min(calc(100% - 75px), 600px)",
			borderRadius: 5,
			position: {
				right: "25px",
				bottom: bottomPosition
			},
			colors: {
				primary: "#900",
				agentMessageBackground: white,
				agentMessageDropdownButtonIcons: white,
				agentMessageText: bodyText,
				customerMessageText: white,
				menuText: "#e6e6e6",
				transcriptBackground: background
			},
			styles: {
				HeaderMenu: {
					padding: "10px"
				},
				HeaderMenuIcons: {
					width: "20px",
					height: "20px",
					marginLeft: "10px",
					lineHeight: "18px",
					color: "#fff"
				},
				HeaderBanner: {
					fontWeight: chatFontWeight,
					backgroundColor: background,
					borderBottom: grayBorder,
					color: bodyText,
					padding: "0 20px",
					minHeight: "40px"
				},
				WelcomeFormBanner: {
					fontWeight: chatFontWeight
				},
				WelcomeFormFieldLabel: {
					fontWeight: chatFontWeight
				},
				WelcomeFormSubmitButton: {
					color: white,
					fontWeight: weightBold,
					borderRadius: "0.4rem",
					padding: "0.5rem 1rem"
				},
				WelcomeFormValidationStyle: {
					background: "#f8d7da",
					border: "1px #f5c6cb solid",
					borderRadius: "0.25rem",
					color: "#721c24",
					padding: ".75rem 1.25rem"
				},
				ErrorBanner: {
					background: "#f8d7da",
					border: "1px #f5c6cb solid",
					borderRadius: "0.25rem",
					color: "#721c24",
					padding: ".75rem 1.25rem"
				},
				AgentAttachmentBubble: {
					border: grayBorder,
					borderRadius: messageRadius
				},
				AgentAvatar: {
					width: "50px",
					height: "50px",
					boxShadow: "none",
					alignSelf: "flex-end"
				},
				AgentMessageBubble: {
					border: grayBorder,
					borderRadius: messageRadius
				},
				AgentMessageText: {
					fontWeight: chatFontWeight
				},
				AgentOpenGraphLink: {
					bubble: {
						color: white,
						fontWeight: chatFontWeight
					}
				},
				AgentRichMessage: {
					body: {
						fontWeight: chatFontWeight
					},
					bubble: {
						borderRadius: messageRadius,
						border: grayBorder
					},
					button: {
						fontWeight: weightBold,
						borderRadius: pillRadius
					},
					title: {
						fontWeight: chatFontWeight
					},
					subtitle: {
						fontWeight: chatFontWeight
					}
				},
				CustomerAttachmentBubble: {
					border: grayBorder,
					borderRadius: messageRadius
				},
				CustomerMessageBubble: {
					borderRadius: pillRadius
				},
				CustomerMessageText: {
					fontWeight: chatFontWeight
				},
				CustomerOpenGraphLink: {
					bubble: {
						fontWeight: chatFontWeight,
						color: white
					}
				},
				CustomerRichMessage: {
					body: {
						fontWeight: chatFontWeight
					},
					title: {
						fontWeight: chatFontWeight
					},
					subtitle: {
						fontWeight: chatFontWeight
					}
				},
				InlineActionButton: {
					background: white,
					fontWeight: weightBold,
					fontSize: "16px",
					borderRadius: pillRadius,
					marginTop: "5px"
				},
				MessageFormInput: {
					fontWeight: chatFontWeight
				},
				MessageFormSend: {
					color: white,
					borderColor: "#600",
					fontWeight: weightBold,
					borderRadius: "0.4rem",
					padding: "0.5rem 1rem",
					marginLeft: "10px",
					marginRight: 0
				},
				OptionsMenuLineItem: {
					fontWeight: chatFontWeight
				}
			},
			messages: {
				headerText: headerText,
				titleText: titleText,
				storageDisabled: "Storage Disabled",
				unsupportedBrowser: "Unsupported Browser"
			}
		};

		this.ConfigureChat();
	}

	public ConfigureChat = (): void =>
	{
		const checkoutChat = document.querySelectorAll(".stlc-sco");
		const noSearchChat = document.querySelectorAll(".stlc-nsrc");
		const siteOptions = this.GetSiteOptions();

		if (typeof this.QuiqOptions !== "undefined" && typeof siteOptions !== "undefined" && siteOptions !== null && Object.keys(siteOptions).length > 0 && siteOptions.constructor === Object)
		{
			try
			{
				this.QuiqOptions = deepmerge(this.QuiqOptions, siteOptions);

				// Always override the (production) contactPoints for lower environments.
				if (window.location.href.indexOf(".test/") > -1
					|| window.location.href.indexOf(".alpha/") > -1
					|| window.location.href.indexOf(".beta/") > -1
					|| window.location.href.indexOf(".train/") > -1
					|| window.location.href.indexOf(".stage/") > -1)
				{
					this.QuiqOptions.contactPoint = "devtesting";
				}

				// If the user (probably) has some kind of active chat going or is on the no search result page
				if (CookieManager.GetCookie(this.ChatCacheCookieName) === "true" || noSearchChat.length)
				{
					this.DownloadChat();
				}
				else
				{
					// Otherwise if not
					// and it's the checkout page
					if (checkoutChat.length)
					{
						checkoutChat.forEach(element =>
						{
							this.ConfigureCheckoutChatButton(element);
						});
					}
					else
					{
						// in all other cases
						this.CheckChatStatus();
					}
				}
			}
			catch
			{
				console.error("Chat option merge error");
				console.error(this.QuiqOptions);
				console.error(siteOptions);
			}
		}
	};

	public GetSiteOptions(): IQuiqOptions
	{
		// return default quiq options
		return this.QuiqOptions;
	}

	public ReinitializeChat = (): void =>
	{
		if (typeof this !== "undefined")
		{
			const chatOnlineClass = "stlc-onl";

			const launchContainers = document.querySelectorAll("[data-chatid=quiq]");
			const customLaunchButtons = [];

			if (launchContainers.length > 0)
			{
				for (let i = 0; i < launchContainers.length; i++)
				{
					customLaunchButtons.push(`#${launchContainers[i].getAttribute("id")} .${chatOnlineClass}`);
				}
			}

			const reinitOptions = {
				customLaunchButtons: customLaunchButtons
			};

			this.QuiqOptions = { ...this.QuiqOptions, ...reinitOptions };

			this.QuiqChat.reinitialize(this.QuiqOptions).then((data) =>
			{
				this.QuiqChat = data;

				this.PostInitializeChat();
			});
		}
	};

	private CheckChatStatus = (): void =>
	{
		const contactPoint = this.QuiqOptions.contactPoint;

		this.GetChatStatus(contactPoint)
			.then((available) =>
			{
				if (available === true)
				{
					this.DownloadButtonsOnline();
					this.ConfigureDownloadButtons();
				}
				else
				{
					this.ShowOfflineButton();

					// Check status again after some interval if chat is offline
					setTimeout(() =>
					{
						this.CheckChatStatus();
					}, this.ChatQueryInterval);
				}
			}).catch((error) =>
			{
				console.log(error);
			});
	};

	private ConfigureDownloadButtons = (): void =>
	{
		const mobileTrigger = document.querySelector("#offCanvasMenu");
		const desktopTrigger = document.querySelector(".contact--head");
		const mobileEvent = "shown.bs.offcanvas";
		const desktopEvent = "shown.bs.dropdown";

		mobileTrigger.addEventListener(mobileEvent, () =>
		{
			desktopTrigger.removeEventListener(desktopEvent, null);

			if (typeof Quiq === "undefined")
			{
				this.DownloadChat();
			}
		}, { once: true });

		desktopTrigger.addEventListener(desktopEvent, () =>
		{
			mobileTrigger.removeEventListener(mobileEvent, null);

			if (typeof Quiq === "undefined")
			{
				this.DownloadChat();
			}
		}, { once: true });
	};

	private ConfigureCheckoutChatButton = (checkoutChat): void =>
	{
		// // if the button hasn't been reached by the user, then set up a lazyload monitor
		// if (!checkoutChat.hasClass("lazyloaded"))
		// {
		// 	// set up lazy loading of quiq on the checkout
		// 	$(document).on("lazybeforeunveil", function (e)
		// 	{
		// 		if ($(e.target).attr("id") === "stlcsco" && typeof this !== "undefined")
		// 		{
		// 			this.DownloadChat();
		// 		}
		// 	});
		// }
		// else
		// {
		// 	// just download quiq if the button is in view
		// 	this.DownloadChat();
		// }
	};

	private DownloadButtonsOnline = (): void =>
	{
		this.ContactLaunchers.forEach(contactLauncher =>
		{
			if (!contactLauncher.classList.contains("btn-success"))
			{
				contactLauncher.classList.remove("btn-secondary");
				contactLauncher.classList.add("btn-success");
			}
		});
	};

	private DownloadButtonsOffline = (): void =>
	{
		this.ContactLaunchers.forEach(contactLauncher =>
		{
			if (contactLauncher.classList.contains("btn-success"))
			{
				contactLauncher.classList.add("btn-secondary");
				contactLauncher.classList.remove("btn-success");
			}
		});
	};

	private DownloadChat = (): void =>
	{
		const buttons = document.querySelectorAll(".contact__chat");

		buttons.forEach(button =>
		{
			button.querySelector("span").textContent = TextResource.GetTextByCulture("Word_Loading") + "...";
		});

		// Check if Quiq SDK has been initialized
		if (typeof Quiq === "undefined")
		{
			// Set up a script tag to download quiq
			const quiqScript = document.createElement("script");
			quiqScript.src = this.QuiqSDKUrl;
			quiqScript.charset = "UTF-8";
			quiqScript.defer = true;
			quiqScript.onload = (): void =>
			{
				// Initialize and open quiq
				this.InitializeChat();
			};

			document.body.appendChild(quiqScript);
		}

		else
		{
			this.ShowOnlineButton();
		}
	};

	private GetChatStatus = (contactPoint: string): Promise<boolean> =>
	{
		const apiUrl = `https://fun.quiq-api.com/api/v1/messaging/agents-available?contactPoint=${contactPoint}&platform=Chat`;

		// API Documentation: https://developers.goquiq.com/api/docs#tag/Messaging

		return axios.get(apiUrl)
			.then((result) =>
			{
				if (typeof result !== "undefined" && typeof result.data !== "undefined" && typeof result.data.available !== "undefined")
				{
					return result.data.available;
				}
			})
			.catch((error) =>
			{
				return false;
			});
	};

	private InitializeChat = (): void =>
	{
		if (typeof Quiq !== "undefined")
		{
			this.QuiqChat = Quiq(this.QuiqOptions);

			this.PostInitializeChat();
		}
	};

	private PostInitializeChat = (): void =>
	{
		// Set cookie to bypass the lazy loading measures when the user interacts with the chat button
		// https://developers.goquiq.com/docs/webchat/#/reference/sdk?id=chatvisibilitydidchange
		this.QuiqChat.on("chatVisibilityDidChange", (e) =>
		{
			if (e.visible === true)
			{
				this.SetChatCache();
			}
		});

		// Setup a handler for detecting agent availability changes
		// https://developers.goquiq.com/docs/webchat/#/reference/sdk?id=agentavailabilitydidchange
		this.QuiqChat.on("agentAvailabilityDidChange", (e) =>
		{
			if (e.available === true)
			{
				this.ShowOnlineButton();
			}
			else
			{
				this.ShowOfflineButton();
			}
		});

		// Verify agent availability
		// https://developers.goquiq.com/docs/webchat/#/reference/sdk?id=getagentavailability
		this.QuiqChat.getAgentAvailability().then((data) =>
		{
			if (typeof this !== "undefined")
			{
				if (data.available)
				{
					this.ShowOnlineButton();
				}
				else
				{
					this.ShowOfflineButton();
				}
			}
		});
	};

	// User (probably) has chat cached
	private SetChatCache = (): void =>
	{
		CookieManager.SetCookie(this.ChatCacheCookieName, "true", 1);
	};

	private ShowOfflineButton = (): void =>
	{
		const buttons = document.querySelectorAll(".contact__chat");
		const icons = document.querySelectorAll<HTMLElement>(".contact__chat svg use");
		const offlineButtons = document.querySelectorAll<HTMLElement>(this.OfflineButtonSelector);
		const onlineButtons = document.querySelectorAll<HTMLElement>(this.OnlineButtonSelector);

		buttons.forEach(button =>
		{
			button.classList.add("disabled");
			button.querySelector("span").textContent = TextResource.GetTextByCulture("Feature_Chat_AllAgentsBusy");
		});

		icons.forEach(icon =>
		{
			const currentAttribute = icon.getAttribute("xlink:href").split("#")[0];
			icon.parentElement.classList.remove("fa-spin");
			icon.setAttribute("xlink:href", `${currentAttribute}#fa-solid-fa-message-slash`);
		});

		offlineButtons.forEach(button =>
		{
			button.style.display = "";
		});

		onlineButtons.forEach(button =>
		{
			button.style.display = "none";
		});

		this.DownloadButtonsOffline();
	};

	private ShowOnlineButton = (): void =>
	{
		const buttons = document.querySelectorAll(".contact__chat");
		const icons = document.querySelectorAll<HTMLElement>(".contact__chat svg use");
		const offlineButtons = document.querySelectorAll<HTMLElement>(this.OfflineButtonSelector);
		const onlineButtons = document.querySelectorAll<HTMLElement>(this.OnlineButtonSelector);

		buttons.forEach(button =>
		{
			button.classList.remove("disabled");
			button.querySelector("span").textContent = TextResource.GetTextByCulture("Feature_Chat_LiveChat");
		});

		icons.forEach(icon =>
		{
			const currentAttribute = icon.getAttribute("xlink:href").split("#")[0];
			icon.setAttribute("xlink:href", `${currentAttribute}#fa-solid-fa-message-dots`);
			icon.parentElement.classList.remove("fa-spin");
		});

		offlineButtons.forEach(button =>
		{
			button.style.display = "none";
		});

		onlineButtons.forEach(button =>
		{
			button.style.display = "";
		});

		this.DownloadButtonsOnline();
	};
}